import type {
  CameraCapabilities,
  CameraCapability,
  CameraControlRequest,
  CameraSettings,
  CapabilityRange,
} from "../protocol/events";
import { clock, log, ms } from "../shared/debug";
import { isIos } from "../shared/platform";

/**
 * The camera: asked for, attached, and released by the scanner.
 *
 * The decoder surface is a `<video>` element the scanner is handed at construction and
 * treats as exactly that — a surface it reads pixels from and drives playback on. It is
 * also an element on the page, positioned by the view under the overlay; that side of
 * it is none of the scanner's business, and nothing here touches `style` or the DOM
 * around it. One element rather than two on one stream, because a second decoder
 * surface is a second native video layer on iOS, and that budget is already spent.
 */
export type CameraSurface = HTMLVideoElement;

/** Keep the native preview fluid while bounding the expensive detector consumer. */
export const CAMERA_FPS = 30;

/**
 * Ask for a sensor raster the device can hold alongside the scanner.
 *
 * Preferred values are `ideal`, with explicit ceilings on frame rate and mobile raster
 * size. Desktop can afford the full raster, but Safari keeps
 * the camera decoder, the native video layer, and the WebGL scan texture alive at once.
 * Three 4K surfaces are enough for iOS to kill the page after a few minutes of scanning.
 */
function constraints(): MediaStreamConstraints {
  // `?cap4k` remains an explicit opt-in for an A/B; `?cap1080` forces the safer mode on
  // every platform. The max is intentional: an iPhone is otherwise allowed to answer an
  // "ideal 1080" request with its 4K mode.
  const capped =
    /[?&]cap1080\b/.test(location.search) || (isIos() && !/[?&]cap4k\b/.test(location.search));
  const size = capped ? { ideal: 1920, max: 1920 } : { ideal: 3840 };
  return {
    audio: false,
    video: {
      facingMode: { ideal: "environment" },
      width: size,
      height: capped ? { ideal: 1080, max: 1080 } : { ideal: 2160 },
      frameRate: { ideal: CAMERA_FPS, max: CAMERA_FPS },
    },
  };
}

export interface Camera {
  stream: MediaStream;
  /** The raster frames arrive at. */
  sensorWidth: number;
  sensorHeight: number;
  /** The track's settings at open — what a `reset` control request returns to. */
  initialSettings: CameraSettings;
}

/* ------------------------------------------------------------ track controls */

/**
 * The track properties a `camera:control` may change, in the order a reset re-applies
 * them: modes first, because a value only holds inside the mode it belongs to.
 */
const CONTROLLABLE = [
  "focusMode",
  "exposureMode",
  "whiteBalanceMode",
  "torch",
  "zoom",
  "exposureCompensation",
  "brightness",
  "contrast",
  "saturation",
  "sharpness",
  "frameRate",
] as const;

/** Identifiers, not capabilities — long, opaque, and nothing a control can use. */
const NOISE = new Set(["deviceId", "groupId"]);

type Loose = Record<string, unknown>;

function jsonCapability(value: unknown): CameraCapability | null {
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean")
    return value;
  if (Array.isArray(value)) {
    if (value.every((item) => typeof item === "string")) return value as string[];
    if (value.every((item) => typeof item === "boolean")) return value as boolean[];
    return null;
  }
  if (value && typeof value === "object") {
    const { min, max, step } = value as Partial<CapabilityRange>;
    if (typeof min === "number" && typeof max === "number")
      return typeof step === "number" ? { min, max, step } : { min, max };
  }
  return null;
}

/** `getSettings()` reduced to what JSON carries, minus the identifiers. */
export function readSettings(track: MediaStreamTrack): CameraSettings {
  const settings: CameraSettings = {};
  for (const [key, value] of Object.entries(track.getSettings() as Loose)) {
    if (NOISE.has(key)) continue;
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean")
      settings[key] = value;
  }
  return settings;
}

/**
 * What the track can do, as far as the browser will say. Firefox has no
 * `getCapabilities` at all; iOS reports a handful; Chrome on Android reports the lot.
 * An empty map is an honest answer, not a failure.
 */
export function describeTrack(track: MediaStreamTrack): {
  label: string;
  capabilities: CameraCapabilities;
  settings: CameraSettings;
} {
  const capabilities: CameraCapabilities = {};
  const raw: Loose =
    typeof track.getCapabilities === "function" ? (track.getCapabilities() as Loose) : {};
  for (const [key, value] of Object.entries(raw)) {
    if (NOISE.has(key)) continue;
    const clean = jsonCapability(value);
    if (clean !== null) capabilities[key] = clean;
  }
  return { label: track.label, capabilities, settings: readSettings(track) };
}

/** Whether a numeric readback counts as the value asked for, at the track's granularity. */
function close(asked: number, got: number, step: number | undefined): boolean {
  const tolerance = Math.max(step ?? 0, Math.abs(asked) * 0.01, 1e-3);
  return Math.abs(got - asked) <= tolerance;
}

/** The step a capability declares for `key`, if it declares one. */
function stepOf(track: MediaStreamTrack, key: string): number | undefined {
  if (typeof track.getCapabilities !== "function") return undefined;
  const capability = (track.getCapabilities() as Loose)[key];
  return capability && typeof capability === "object" && !Array.isArray(capability)
    ? (capability as CapabilityRange).step
    : undefined;
}

/**
 * Apply one control request and report what the track says afterwards.
 *
 * Sent as an `advanced` constraint set, which is the form every browser that has these
 * properties accepts — a bare `torch: true` was refused by Chrome for years. The catch
 * is that an advanced set the track cannot satisfy is *dropped silently*, so a resolved
 * promise proves nothing. Every requested property is therefore read back from
 * `getSettings()`, and `ok` means all of them came back as asked. `pointsOfInterest` is
 * never reported back by any browser and is taken on trust.
 *
 * A reset walks the controllable properties one set at a time, so one that the track
 * refuses cannot take the others down with it.
 */
export async function applyControls(
  track: MediaStreamTrack,
  request: CameraControlRequest,
  initial: CameraSettings,
): Promise<{ ok: boolean; message: string; settings: CameraSettings }> {
  const sets: Loose[] = [];
  if (request.reset) {
    // Only what the track admits to: asking a camera without a torch to turn its torch
    // off is an OverconstrainedError, not a no-op. Without `getCapabilities` (Firefox)
    // the settings at open are the only word on what exists.
    const supported =
      typeof track.getCapabilities === "function"
        ? new Set(Object.keys(track.getCapabilities() as Loose))
        : null;
    for (const key of CONTROLLABLE) {
      if (supported && !supported.has(key)) continue;
      if (key in initial) sets.push({ [key]: initial[key] });
      // A torch is never on at open, but a browser that omits it from the settings
      // would otherwise leave it lit.
      else if (key === "torch" && supported?.has("torch")) sets.push({ torch: false });
    }
  }
  const asked: Loose = {};
  for (const [key, value] of Object.entries(request)) {
    if (key !== "reset" && value !== undefined) asked[key] = value;
  }
  if (Object.keys(asked).length) sets.push(asked);
  if (!sets.length) return { ok: true, message: "nothing to apply", settings: readSettings(track) };

  const problems: string[] = [];
  for (const set of sets) {
    try {
      await track.applyConstraints({ advanced: [set] } as unknown as MediaTrackConstraints);
    } catch (error) {
      // A reset keeps going: the property that threw is reported, the rest still land.
      if (!request.reset) throw error;
      problems.push(`${Object.keys(set).join(",")}: ${(error as Error).message}`);
    }
  }

  const settings = readSettings(track);
  for (const [key, value] of Object.entries(asked)) {
    if (key === "pointsOfInterest") continue;
    const got = settings[key];
    if (got === undefined) problems.push(`${key} not reported back`);
    else if (typeof value === "number" && typeof got === "number") {
      if (!close(value, got, stepOf(track, key))) problems.push(`${key} read back as ${got}`);
    } else if (got !== value) problems.push(`${key} stayed ${String(got)}`);
  }
  const trusted = "pointsOfInterest" in asked ? "point of interest sent (never reported back)" : "";
  return {
    ok: !problems.length,
    message: [...problems, trusted].filter(Boolean).join("; "),
    settings,
  };
}

/** How long to wait for the decoder to report its raster before giving up on the wait. */
const METADATA_WAIT_MS = 5000;

/**
 * Open the camera onto `surface` and play it. Resolves once frames are flowing and the
 * raster is known, which is what the view needs to frame anything.
 */
export async function openCamera(surface: CameraSurface): Promise<Camera> {
  const opened = clock();
  const stream = await navigator.mediaDevices.getUserMedia(constraints());
  // What was asked for and what the device answered with are routinely different, and the
  // difference explains most "why is detection worse on this phone" questions.
  const track = stream.getVideoTracks()[0];
  log(
    "detect",
    `camera opened in ${ms(opened())} — "${track?.label ?? "?"}" ` +
      `(requested ${CAMERA_FPS} fps preview)`,
    track?.getSettings(),
  );

  // Both are required on iOS: without them Safari opens the stream fullscreen in its own
  // player instead of letting the page composite it.
  surface.playsInline = true;
  surface.muted = true;
  surface.srcObject = stream;
  await surface.play();

  // `play()` resolves once playback has begun, which means the metadata is normally in
  // by now. A decoder that is slower than that reports its raster with a `resize`, so
  // wait for it — bounded, because a camera that never says is broken in a way the
  // scan loop's own guards handle better than a hang here would.
  if (!surface.videoWidth || !surface.videoHeight) {
    await new Promise<void>((resolve) => {
      const done = () => {
        surface.removeEventListener("resize", done);
        surface.removeEventListener("loadedmetadata", done);
        clearTimeout(timer);
        resolve();
      };
      const timer = setTimeout(done, METADATA_WAIT_MS);
      surface.addEventListener("resize", done);
      surface.addEventListener("loadedmetadata", done);
    });
  }

  return {
    stream,
    sensorWidth: surface.videoWidth,
    sensorHeight: surface.videoHeight,
    initialSettings: track ? readSettings(track) : {},
  };
}

/** Release the stream and detach it. The element stays; the view clears its own styling. */
export function closeCamera(surface: CameraSurface, camera: Camera): void {
  camera.stream.getTracks().forEach((t) => t.stop());
  surface.pause();
  surface.srcObject = null;
}

/**
 * Hear the device end the track under an open camera.
 *
 * `ended` fires when a track ends for any reason *other than* its own `stop()` — another
 * app taking the camera, the browser's camera control in its address bar, the system
 * reclaiming it — so releasing the camera through {@link closeCamera} never reports as
 * a loss. Returns the unwatch, for the release.
 */
export function watchCamera(camera: Camera, onEnded: () => void): () => void {
  const track = camera.stream.getVideoTracks()[0];
  if (!track) return () => {};
  track.addEventListener("ended", onEnded);
  return () => track.removeEventListener("ended", onEnded);
}
